import { LogEntry, LogTransport, LogLevel } from '../types';
import { LOG_CONFIG } from '../config';
import { isCapacitorApp } from '../../platform';

// Import Capacitor plugins if available
let Capacitor: any;
let Http: any;
let Device: any;
try {
  Capacitor = require('@capacitor/core').Capacitor;
  Http = require('@capacitor/core').Http;
  Device = require('@capacitor/device').Device;
} catch (e) {
  // Silently fail if Capacitor is not available
}

// Helper function to log to Android logcat with a distinctive tag
// Only used in mobile environments for critical logs
function logToAndroid(message: string, data?: any): void {
  if (!isCapacitorApp()) return;
  
  const tag = 'LOKI_LOGGER';
  if (data) {
    console.log(`${tag}: ${message}`, data);
  } else {
    console.log(`${tag}: ${message}`);
  }
}

export class LokiTransport implements LogTransport {
  private readonly host: string;
  private readonly sourceName: string;
  private readonly auth: { username: string; password: string };
  private buffer: LogEntry[] = [];
  private flushTimeout: NodeJS.Timeout | null = null;
  private isMobileApp: boolean;
  private deviceInfo: any = null;
  private shouldSendTestLog: boolean;

  constructor() {
    this.isMobileApp = isCapacitorApp();
    
    // In development, we'll use the proxy endpoint
    this.host = import.meta.env.DEV ? '' : LOG_CONFIG.loki.host;
    this.sourceName = LOG_CONFIG.loki.sourceName;
    this.auth = LOG_CONFIG.loki.auth;
    
    // Determine if we should send test logs based on configuration
    this.shouldSendTestLog = LOG_CONFIG.loki.sendTestLogs === true;
    
    this.setupAutoFlush();
    
    // Only send test log when running in a mobile environment AND test logs are enabled
    if (this.isMobileApp && this.shouldSendTestLog) {
      // Send test log with a slight delay to ensure initialization is complete
      setTimeout(() => {
        this.getDeviceInfoAndSendTestLog();
      }, 2000);
    }
  }

  private getLokiUrl(): string {
    // In development, use the proxy endpoint
    if (import.meta.env.DEV) {
      return '/api/logs';
    }
    // In production, use the full Loki URL
    return `${this.host}/loki/api/v1/push`;
  }

  private setupAutoFlush(): void {
    if (this.flushTimeout) {
      clearInterval(this.flushTimeout);
    }
    
    this.flushTimeout = setInterval(
      () => this.flush(),
      LOG_CONFIG.general.flushInterval
    );
  }

  private async flush(): Promise<void> {
    if (this.buffer.length === 0) return;

    const entries = [...this.buffer];
    this.buffer = [];
    await this.sendBatch(entries);
  }

  private createLogLine(entry: LogEntry): string {
    const logData = {
      level: entry.level.toLowerCase(),
      msg: entry.message,
      ...entry.metadata,
      ...(entry.error ? {
        error: {
          name: entry.error.name,
          message: entry.error.message,
          ...(import.meta.env.DEV ? { stack: entry.error.stack } : {}),
          ...(entry.error instanceof Error && (entry.error as any).metadata ? {
            metadata: (entry.error as any).metadata
          } : {})
        }
      } : {})
    };
    
    const logString = JSON.stringify(logData);
    return LOG_CONFIG.redactionPatterns.reduce(
      (acc, { pattern, replacement }) => acc.replace(pattern, replacement),
      logString
    );
  }

  private formatEntries(entries: LogEntry[]): any {
    const streamGroups = new Map<string, { stream: Record<string, string>; values: [string, string][] }>();

    entries.forEach(entry => {
      const component = entry.metadata?.component || entry.labels?.component || 'general';
      
      const stream = {
        level: entry.level.toLowerCase(),
        source: this.sourceName,
        env: LOG_CONFIG.loki.labels.environment,
        component,
        job: 'frontend',
        version: LOG_CONFIG.loki.labels.version
      };

      const streamKey = Object.entries(stream)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => `${k}=${v}`)
        .join(',');

      let group = streamGroups.get(streamKey);
      if (!group) {
        group = { stream, values: [] };
        streamGroups.set(streamKey, group);
      }

      const timestamp = (Date.now() * 1000000).toString();
      const logLine = this.createLogLine(entry);
      group.values.push([timestamp, logLine]);
    });

    return {
      streams: Array.from(streamGroups.values())
    };
  }

  async send(entry: LogEntry): Promise<void> {
    // Skip if Loki is disabled
    if (!LOG_CONFIG.loki.enabled) return;
    
    this.buffer.push(entry);

    if (this.buffer.length >= LOG_CONFIG.general.batchSize) {
      await this.flush();
    }
  }

  async sendBatch(entries: LogEntry[]): Promise<void> {
    // Skip if Loki is disabled
    if (!LOG_CONFIG.loki.enabled) return;

    if ((!this.host && !import.meta.env.DEV) || !this.auth.username || !this.auth.password) {
      // Only log in development
      if (import.meta.env.DEV) {
        console.error('Loki configuration missing');
      }
      return;
    }

    try {
      const requestBody = this.formatEntries(entries);
      const url = this.getLokiUrl();
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${this.auth.username}:${this.auth.password}`),
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok && response.status !== 204) {
        // Only log in development
        if (import.meta.env.DEV) {
          const responseText = await response.text();
          console.error(`Failed to send logs: ${response.statusText}`);
        }
      }
    } catch (error) {
      // Only log in development
      if (import.meta.env.DEV) {
        console.error('Error sending logs to Loki');
      }
    }
  }

  destroy(): void {
    if (this.flushTimeout) {
      clearInterval(this.flushTimeout);
      this.flushTimeout = null;
    }
    // Flush any remaining logs before destroying
    this.flush().catch(() => {
      // Silently fail
    });
  }

  private async getDeviceInfoAndSendTestLog(): Promise<void> {
    // Skip if test logs are disabled
    if (!this.shouldSendTestLog) return;
    
    try {
      // Get device info if we're in a Capacitor environment
      if (this.isMobileApp && Device) {
        try {
          const info = await Device.getInfo();
          const battery = await Device.getBatteryInfo();
          this.deviceInfo = {
            ...info,
            battery,
            capacitorVersion: Capacitor.VERSION
          };
        } catch (deviceError) {
          // Silently fail
        }
      }
      
      // Proceed with network check and test log
      await this.checkNetworkAndSendTestLog();
    } catch (error) {
      // Silently fail
    }
  }

  private async checkNetworkAndSendTestLog(): Promise<void> {
    // Skip if test logs are disabled
    if (!this.shouldSendTestLog) return;
    
    try {
      // First, check if Loki is enabled
      if (!LOG_CONFIG.loki.enabled) return;
      
      // Check if we have the necessary configuration
      if ((!this.host && !import.meta.env.DEV) || !this.auth.username || !this.auth.password) {
        return;
      }
      
      // Try to ping a reliable endpoint to verify connectivity
      try {
        if (this.isMobileApp && Http) {
          // Use Capacitor HTTP plugin for mobile
          await Http.get({
            url: 'https://www.google.com/generate_204',
            method: 'HEAD'
          });
        } else {
          // Use standard fetch for web
          await fetch('https://www.google.com/generate_204', { 
            method: 'HEAD',
            mode: 'no-cors',
            cache: 'no-cache'
          });
        }
      } catch (pingError) {
        // Continue anyway
      }
      
      // Proceed with sending the test log
      await this.sendTestLog();
      
    } catch (error) {
      // Silently fail
    }
  }

  private async sendTestLog(): Promise<void> {
    // Skip if test logs are disabled
    if (!this.shouldSendTestLog) return;
    
    try {
      // Create a simple test entry
      const testEntry: LogEntry = {
        timestamp: new Date().toISOString(),
        level: LogLevel.INFO,
        message: 'MOBILE TEST LOG - PLEASE VERIFY',
        labels: {
          test: 'true',
          source: this.sourceName,
          platform: 'mobile',
          timestamp: Date.now().toString()
        },
        metadata: {
          host: this.host || '(using proxy)',
          timestamp: Date.now(),
          isMobile: true,
          deviceInfo: this.deviceInfo || {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            vendor: navigator.vendor
          }
        }
      };

      // Get the Loki URL
      const url = this.getLokiUrl();

      // Format the entry for Loki
      const requestBody = this.formatEntries([testEntry]);

      // Try multiple approaches to send the log
      let success = false;
      let lastError: any = null;

      // Approach 1: Use Capacitor HTTP plugin if available
      if (this.isMobileApp && Http) {
        try {
          const response = await Http.request({
            method: 'POST',
            url: url,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Basic ' + btoa(`${this.auth.username}:${this.auth.password}`)
            },
            data: requestBody
          });
          
          if (response.status >= 200 && response.status < 300) {
            success = true;
          } else {
            lastError = new Error(`HTTP status ${response.status}`);
          }
        } catch (error) {
          lastError = error;
        }
      }

      // Approach 2: Use standard fetch
      if (!success) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': 'Basic ' + btoa(`${this.auth.username}:${this.auth.password}`),
            },
            body: JSON.stringify(requestBody)
          });
          
          if (response.ok || response.status === 204) {
            success = true;
          } else {
            lastError = new Error(`HTTP status ${response.status}: ${response.statusText}`);
          }
        } catch (error) {
          lastError = error;
        }
      }

      // Approach 3: Use XMLHttpRequest as a fallback
      if (!success) {
        try {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('Authorization', 'Basic ' + btoa(`${this.auth.username}:${this.auth.password}`));
          
          // Set up promise to handle async XHR
          const xhrPromise = new Promise<{status: number, response: string}>((resolve, reject) => {
            xhr.onload = function() {
              resolve({
                status: xhr.status,
                response: xhr.responseText
              });
            };
            
            xhr.onerror = function() {
              reject(new Error('XHR request failed'));
            };
          });
          
          // Send the request
          xhr.send(JSON.stringify(requestBody));
          
          // Wait for response
          const result = await xhrPromise;
          
          if (result.status >= 200 && result.status < 300 || result.status === 204) {
            success = true;
          } else {
            lastError = new Error(`XHR status ${result.status}`);
          }
        } catch (error) {
          lastError = error;
        }
      }

      // If successful, also send a regular log through the normal channel
      if (success) {
        this.send({
          timestamp: new Date().toISOString(),
          level: LogLevel.INFO,
          message: 'Mobile test log sent through regular channel',
          labels: {
            test: 'true',
            source: this.sourceName
          },
          metadata: {
            isMobile: true,
            sentAt: new Date().toISOString(),
            deviceInfo: this.deviceInfo
          }
        });
      }
    } catch (error) {
      // Silently fail in production
    }
  }
} 
